import { createCapture } from "./capture.js";
import { mountUI, type UIHandle } from "./ui.js";
import { submitReport, fetchConfig } from "./transport.js";
import type { InitOptions, PublicAPI, Reporter, WidgetConfig } from "./types.js";

type Pending = { type: "identify"; user: Reporter } | { type: "setContext"; data: Record<string, unknown> } | { type: "open" } | { type: "track"; event: string };

declare global {
  interface Window {
    FeedbackTool?: (PublicAPI & InitOptions) | Partial<PublicAPI & InitOptions>;
  }
}

(function bootstrap() {
  if (typeof window === "undefined") return;
  const existing = window.FeedbackTool as (InitOptions & { _pending?: Pending[] }) | undefined;
  if (!existing || !existing.appId) {
    // No config yet — allow future init.
    if (!existing) window.FeedbackTool = {};
    return;
  }
  if ((existing as { _booted?: boolean })._booted) return;
  (existing as { _booted?: boolean })._booted = true;

  const appId = existing.appId;
  const apiBase =
    existing.apiBase ||
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ((globalThis as any).__FEEDBASE_API_BASE__ as string | undefined) ||
    "http://localhost:4000";

  const capture = createCapture();
  let reporter: Reporter | undefined;
  let customContext: Record<string, unknown> = {};
  let ui: UIHandle | undefined;
  let config: WidgetConfig | undefined;

  async function submit(
    message: string,
    type: "bug" | "feature" | "other",
    ctx: { includeScreenshot: boolean },
  ) {
    const snap = capture.snapshot();
    let screenshotS3Key: string | null = null;
    if (config?.captureScreenshots && ctx.includeScreenshot) {
      try {
        const { captureScreenshot } = await import("./screenshot.js");
        screenshotS3Key = await captureScreenshot(apiBase, appId);
      } catch (err) {
        console.warn("[FeedbackTool] screenshot failed", err);
      }
    }

    await submitReport(apiBase, appId, {
      message,
      type,
      pageUrl: location.href,
      route: location.pathname,
      userAgent: navigator.userAgent,
      osName: snap.osName,
      browserName: snap.browserName,
      screenW: window.innerWidth,
      screenH: window.innerHeight,
      reporter,
      context: {
        screenshotS3Key,
        consoleLogs: config?.captureConsole === false ? [] : snap.consoleLogs,
        networkErrors: config?.captureNetwork === false ? [] : snap.networkErrors,
        breadcrumbs: snap.breadcrumbs,
        extra: customContext,
      },
    });
  }

  const api: PublicAPI = {
    identify(user) {
      reporter = user;
    },
    setContext(data) {
      customContext = { ...customContext, ...data };
    },
    open() {
      ui?.open();
    },
    track(event) {
      capture.breadcrumb("custom", event);
    },
  };

  fetchConfig(apiBase, appId)
    .then((cfg) => {
      config = cfg;
      ui = mountUI({
        onSubmit: submit,
        screenshotsAvailable: !!cfg.captureScreenshots,
      });
      // flush pending
      const pending = existing._pending ?? [];
      for (const p of pending) {
        if (p.type === "identify") api.identify(p.user);
        else if (p.type === "setContext") api.setContext(p.data);
        else if (p.type === "open") api.open();
        else if (p.type === "track") api.track(p.event);
      }
      // swap global stub with real API
      window.FeedbackTool = Object.assign(existing, api);
    })
    .catch((err) => {
      console.warn("[FeedbackTool] failed to load config", err);
    });
})();
